import React, { useEffect } from "react";
import { Box, Divider, Link, List, ListItem, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const ExternalLink = ({ href, children }: { href: string; children: React.ReactNode }) => (
  <Link component="a" href={href} target="_blank" rel="noreferrer">{children}</Link>
);

const AccessibilityPage = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (i18n.language !== "fr") navigate("/shops");
  }, [i18n.language, navigate]);

  return (
    <Box component="main" sx={{ maxWidth: 900, mx: "auto", py: 4, px: 2 }}>
      <Typography component="h1" variant="h4" fontWeight={700} gutterBottom>
        Déclaration d'accessibilité
      </Typography>

      <Typography paragraph>
        Cette déclaration a été rédigée le 16/02/2026 pour le site{" "}
        <ExternalLink href="https://merch.thomgroup.com">https://merch.thomgroup.com</ExternalLink>.
      </Typography>
      <Typography paragraph>
        Elle est valide dès sa publication, doit être actualisée après 3 ans, à la suite de
        modifications majeures du site, ou 18 mois après une nouvelle version du référentiel.
      </Typography>

      <Divider sx={{ my: 3 }} />

      <Typography component="h2" variant="h5" fontWeight={700} gutterBottom>
        Thom Group s'engage
      </Typography>
      <Typography paragraph>
        <ExternalLink href="https://www.thomgroup.com">Thom Group</ExternalLink> s'engage à rendre ses sites
        internet, applications mobiles, intranet, extranet et ses progiciels accessibles
        conformément à l'article 47 de la loi n°2005-102 du 11 février 2005.
      </Typography>
      <Typography paragraph>
        Nous valorisons l'inclusion et la diversité, et nous nous assurons que notre site Web :
        https://merch.thomgroup.com est accessible à tous, notamment aux personnes en situation de
        handicap.
      </Typography>
      <Typography paragraph>
        Dans cette démarche d'accessibilité numérique, nous collaborons avec{" "}
        <ExternalLink href="https://www.accessiway.com">Accessiway</ExternalLink>.
      </Typography>
      <Typography paragraph>
        Nous sommes résolument engagés envers un web pour tous. Nous aspirons, dans une vision à
        long terme et responsable, à répondre pleinement aux exigences réglementaires, notamment le{" "}
        <ExternalLink href="https://accessibilite.numerique.gouv.fr/methode/criteres-et-tests/">RGAA 4.1.2</ExternalLink>{" "}
        et à la norme européenne{" "}
        <ExternalLink href="https://www.etsi.org/deliver/etsi_en/301500_301599/301549/03.02.01_60/en_301549v030201p.pdf">EN 301 549</ExternalLink>.
      </Typography>

      <Divider sx={{ my: 3 }} />

      <Typography component="h2" variant="h5" fontWeight={700} gutterBottom>
        État de conformité
      </Typography>
      <Typography paragraph>
        Le site Web :{" "}
        <ExternalLink href="https://merch.thomgroup.com">https://merch.thomgroup.com</ExternalLink> possède un taux
        d'accessibilité de <strong>58,00%</strong> et est donc{" "}
        <strong>partiellement conforme</strong> avec le référentiel général d'amélioration de
        l'accessibilité (RGAA 4.1.2).
      </Typography>

      <Divider sx={{ my: 3 }} />

      <Typography component="h2" variant="h5" fontWeight={700} gutterBottom>
        Résultats des tests
      </Typography>
      <Typography paragraph>
        L'audit de conformité réalisé le 16/02/2026 par{" "}
        <ExternalLink href="https://www.accessiway.com">AccessiWay</ExternalLink> révèle que 58,00% des critères
        du RGAA version 4.1.2 sont respectés.
      </Typography>
      <Typography gutterBottom>Détails des résultats :</Typography>
      <List dense sx={{ pl: 2 }}>
        <ListItem sx={{ display: "list-item", listStyleType: "disc" }}>Nombre de critères conformes : 29</ListItem>
        <ListItem sx={{ display: "list-item", listStyleType: "disc" }}>Nombre de critères non conformes : 21</ListItem>
        <ListItem sx={{ display: "list-item", listStyleType: "disc" }}>Nombre de critères non applicables : 56</ListItem>
      </List>

      <Divider sx={{ my: 3 }} />

      <Typography component="h2" variant="h5" fontWeight={700} gutterBottom>
        Contenu non accessible
      </Typography>
      <Typography paragraph>
        Les contenus listés ci-dessous ne sont pas accessibles pour les raisons suivantes.
      </Typography>

      <Typography component="h3" variant="h6" fontWeight={700} gutterBottom>
        Non conformité
      </Typography>
      <List dense sx={{ pl: 2 }}>
        {[
          "3.1 – Dans chaque page web, l'information ne doit pas être donnée uniquement par la couleur. Cette règle est-elle respectée ? : Sur certaines pages web, l'information est donnée uniquement par la couleur.",
          "3.2 – Dans chaque page web, le contraste entre la couleur du texte et la couleur de son arrière-plan est-il suffisamment élevé (hors cas particuliers) ? : Le contraste entre le texte et son arrière-plan est insuffisant sur certaines pages web.",
          "3.3 – Dans chaque page web, les couleurs utilisées dans les composants d'interface ou les éléments graphiques porteurs d'informations sont-elles suffisamment contrastées (hors cas particuliers) ? : Les couleurs utilisées dans certains composants d'interface ou éléments graphiques porteurs d'informations ne présentent pas un contraste suffisant.",
          "7.1 – Chaque script est-il, si nécessaire, compatible avec les technologies d'assistance ? : Certains scripts ne sont pas compatibles avec les technologies d'assistance lorsque cela est nécessaire.",
          "7.3 – Chaque script est-il contrôlable par le clavier et par tout dispositif de pointage (hors cas particuliers) ? : Certains scripts ne sont pas contrôlables via le clavier ou tout dispositif de pointage.",
          "7.5 – Dans chaque page web, les messages de statut sont-ils correctement restitués par les technologies d'assistance ? : Certains messages de statut ne sont pas correctement restitués par les technologies d'assistance.",
          "8.3 – Dans chaque page web, la langue par défaut est-elle présente ? : Certaines pages web n'indiquent pas la langue par défaut.",
          "8.6 – Pour chaque page web ayant un titre de page, ce titre est-il pertinent ? : Certains titres de page ne sont pas pertinents par rapport au contenu.",
          "9.1 – Dans chaque page web, l'information est-elle structurée par l'utilisation appropriée de titres ? : L'information n'est pas correctement structurée par l'utilisation appropriée de titres sur certaines pages.",
          "9.2 – Dans chaque page web, la structure du document est-elle cohérente (hors cas particuliers) ? : La structure de certaines pages web n'est pas cohérente.",
          "9.3 – Dans chaque page web, chaque liste est-elle correctement structurée ? : Certaines listes présentes sur les pages web ne sont pas correctement structurées.",
          "10.6 – Dans chaque page web, chaque lien dont la nature n'est pas évidente est-il visible par rapport au texte environnant ? : Certains liens dont la nature n'est pas évidente ne sont pas visibles par rapport au texte environnant.",
          "10.11 – Pour chaque page web, les contenus peuvent-ils être présentés sans avoir recours à la fois à un défilement vertical pour une fenêtre ayant une hauteur de 256px ou à un défilement horizontal pour une fenêtre ayant une largeur de 320px (hors cas particuliers) ? : Certains contenus nécessitent un défilement vertical ou horizontal excessif pour être consultés.",
          "10.13 – Dans chaque page web, les contenus additionnels apparaissant à la prise de focus ou au survol d'un composant d'interface sont-ils contrôlables par l'utilisateur (hors cas particuliers) ? : Certains contenus additionnels apparaissant à la prise de focus ou au survol d'un composant d'interface ne sont pas contrôlables par l'utilisateur.",
          "11.1 – Chaque champ de formulaire a-t-il une étiquette ? : Certains champs de formulaire ne disposent pas d'étiquette.",
          "11.10 – Dans chaque formulaire, le contrôle de saisie est-il utilisé de manière pertinente (hors cas particuliers) ? : Certains contrôles de saisie ne sont pas utilisés de manière pertinente.",
          "11.11 – Dans chaque formulaire, le contrôle de saisie est-il accompagné, si nécessaire, de suggestions facilitant la correction des erreurs de saisie ? : Certains contrôles de saisie ne sont pas accompagnés de suggestions facilitant la correction des erreurs lorsque cela est nécessaire.",
          "11.13 – La finalité d'un champ de saisie peut-elle être déduite pour faciliter le remplissage automatique des champs avec les données de l'utilisateur ? : La finalité de certains champs de saisie ne peut pas être déduite pour faciliter le remplissage automatique avec les données de l'utilisateur.",
          "12.7 – Dans chaque page web, un lien d'évitement ou d'accès rapide à la zone de contenu principal est-il présent (hors cas particuliers) ? : Un lien d'évitement ou d'accès rapide à la zone de contenu principal n'est pas présent sur certaines pages.",
          "12.11 – Dans chaque page web, les contenus additionnels apparaissant au survol, à la prise de focus ou à l'activation d'un composant d'interface sont-ils si nécessaire atteignables au clavier ? : Certains contenus additionnels apparaissant au survol, à la prise de focus ou à l'activation d'un composant d'interface ne sont pas atteignables au clavier.",
          "13.8 – Dans chaque page web, chaque contenu en mouvement ou clignotant est-il contrôlable par l'utilisateur ? : Certains contenus en mouvement ou clignotants ne sont pas contrôlables par l'utilisateur.",
        ].map((item, i) => (
          <ListItem key={i} sx={{ display: "list-item", listStyleType: "disc", alignItems: "flex-start" }}>
            <Typography variant="body2">{item}</Typography>
          </ListItem>
        ))}
      </List>

      <Typography component="h3" variant="h6" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
        Dérogations pour charge disproportionnée
      </Typography>
      <Typography paragraph>Aucun contenu à mentionner</Typography>

      <Typography component="h3" variant="h6" fontWeight={700} gutterBottom>
        Contenus non soumis à l'obligation d'accessibilité
      </Typography>
      <Typography paragraph>Aucun contenu à mentionner</Typography>

      <Divider sx={{ my: 3 }} />

      <Typography component="h2" variant="h5" fontWeight={700} gutterBottom>
        Établissement de cette déclaration d'accessibilité
      </Typography>
      <Typography paragraph>Cette déclaration a été établie le 16/02/2026.</Typography>

      <Divider sx={{ my: 3 }} />

      <Typography component="h2" variant="h5" fontWeight={700} gutterBottom>
        Technologies utilisées pour la réalisation du site
      </Typography>
      <List dense sx={{ pl: 2 }}>
        {["HTML5", "CSS", "Javascript"].map((tech) => (
          <ListItem key={tech} sx={{ display: "list-item", listStyleType: "disc" }}>
            {tech}
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 3 }} />

      <Typography component="h2" variant="h5" fontWeight={700} gutterBottom>
        Environnement de test
      </Typography>

      <Typography component="h3" variant="h6" fontWeight={700} gutterBottom>
        Systèmes d'exploitation
      </Typography>
      <Typography gutterBottom>Dans les dernières versions disponibles :</Typography>
      <List dense sx={{ pl: 2 }}>
        {["Apple Mac Os", "Microsoft Windows", "Apple iOS", "Google Android"].map((os) => (
          <ListItem key={os} sx={{ display: "list-item", listStyleType: "disc" }}>
            {os}
          </ListItem>
        ))}
      </List>

      <Typography component="h3" variant="h6" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
        Navigateurs et softwares
      </Typography>
      <Typography gutterBottom>
        Dans les dernières versions disponibles sur les différents systèmes d'exploitation :
      </Typography>
      <List dense sx={{ pl: 2 }}>
        {[
          "Google Chrome",
          "Windows Edge",
          "Safari",
          "Brave",
          "Adobe Acrobat Reader / Preview on Mac (pour PDFs uniquement)",
        ].map((browser) => (
          <ListItem key={browser} sx={{ display: "list-item", listStyleType: "disc" }}>
            {browser}
          </ListItem>
        ))}
      </List>

      <Typography component="h3" variant="h6" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
        Outils pour évaluer l'accessibilité
      </Typography>
      <List dense sx={{ pl: 2 }}>
        {[
          "Emulations de souris, loupes et claviers à l'écran des différents systèmes.",
          "Voiceover (Apple systems uniquement)",
          "Talkback (Android uniquement)",
          "NVDA 2025 et Freedom scientific Jaws 2022 (PC systems uniquement)",
          "Tanaguru webext RGAA4",
          "Assistant RGAA",
          "Outils d'évaluations graphiques présentes sur les différents systèmes (couleurs, contrastes, sous-titres, etc.)",
        ].map((tool, i) => (
          <ListItem key={i} sx={{ display: "list-item", listStyleType: "disc" }}>
            <Typography variant="body2">{tool}</Typography>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 3 }} />

      <Typography component="h2" variant="h5" fontWeight={700} gutterBottom>
        Pages du site ayant fait l'objet de la vérification de conformité
      </Typography>
      <List dense sx={{ pl: 2 }}>
        {[
          "Se connecter",
          "Magasins",
          "Recenser",
          "Comparer",
          "Parcours clients",
          "Statistiques",
        ].map((page) => (
          <ListItem key={page} sx={{ display: "list-item", listStyleType: "disc" }}>
            {page}
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 3 }} />

      <Typography component="h2" variant="h5" fontWeight={700} gutterBottom>
        Retour d'information et contact
      </Typography>
      <Typography paragraph>
        Si vous n'arrivez pas à accéder à un contenu ou à un service, vous pouvez contacter le
        responsable de l'accessibilité de <strong>Thom Merch,</strong> Leslie BUFFIGNANI pour être
        orienté vers une alternative accessible ou obtenir le contenu sous une autre forme.
      </Typography>

      <Divider sx={{ my: 3 }} />

      <Typography component="h2" variant="h5" fontWeight={700} gutterBottom>
        Voies de recours
      </Typography>
      <Typography paragraph>
        Si vous constatez un défaut d'accessibilité vous empêchant d'accéder à un contenu ou une
        fonctionnalité du site et qu'après nous l'avoir signalé vous ne parvenez pas à obtenir une
        réponse de notre part, vous êtes en droit de faire parvenir vos doléances ou une demande de
        saisine au Défenseur des droits.
      </Typography>
      <Typography gutterBottom>Plusieurs moyens sont à votre disposition :</Typography>
      <List dense sx={{ pl: 2 }}>
        <ListItem sx={{ display: "list-item", listStyleType: "disc" }}>
          <ExternalLink href="https://formulaire.defenseurdesdroits.fr/">
            Écrire un message au Défenseur des droits (nouvelle fenêtre)
          </ExternalLink>
        </ListItem>
        <ListItem sx={{ display: "list-item", listStyleType: "disc" }}>
          <ExternalLink href="https://www.defenseurdesdroits.fr/fr/saisir/delegues">
            Contacter le délégué du Défenseur des droits dans votre région (nouvelle fenêtre)
          </ExternalLink>
        </ListItem>
        <ListItem sx={{ display: "list-item", listStyleType: "disc" }}>
          <Typography variant="body2">
            Envoyer un courrier (gratuit, ne pas mettre de timbre) :<br />
            Défenseur des droits<br />
            Libre réponse 71120<br />
            75342 Paris CEDEX 07<br />
            France
          </Typography>
        </ListItem>
      </List>
    </Box>
  );
};

export default AccessibilityPage;